import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spinKf = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/* ============================================================
 * MIXINS
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut},
              transform ${tokens.durBase} ${tokens.easeOut},
              box-shadow ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER — mesh background fixo, max-width 90em (igual Files)
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 0 2em;
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  animation: ${fadeInUp} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  .spin { animation: ${spinKf} 1s linear infinite; }

  /* ============================================================
   * HEADER
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "ADMINISTRAÇÃO ELASTICSEARCH · CLUSTER ATIVO";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
          }

          &.green {
            color: ${tokens.brand};
            &::before {
              background: ${tokens.brand};
              box-shadow: 0 0 8px ${tokens.brandGlow};
              animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
            }
          }
          &.yellow {
            color: ${tokens.warn};
            &::before {
              background: ${tokens.warn};
              box-shadow: 0 0 8px rgba(245,158,11,0.45);
            }
          }
          &.red {
            color: ${tokens.danger};
            &::before {
              background: ${tokens.danger};
              box-shadow: 0 0 8px rgba(239,68,68,0.5);
            }
          }
          &.default, &.loading {
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            &::before {
              background: ${({ theme }) => theme.text};
              opacity: 0.3;
            }
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ============================================================
   * KPI CARDS — 5 hero cards (igual Files main-stats)
   * ============================================================ */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
    margin-bottom: 20px;
    flex-shrink: 0;

    @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 700px)  { grid-template-columns: repeat(2, 1fr); }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p { margin: 0; }

        p:first-child {
          ${mono};
          font-size: 1.6em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        p:last-child {
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 2px;
          letter-spacing: -0.005em;
        }
      }
    }

    /* Variantes de cor sutis no número principal */
    &.success .card-content .text p:first-child { color: ${tokens.brand}; }
    &.warning .card-content .text p:first-child { color: ${tokens.warn}; }
    &.danger  .card-content .text p:first-child { color: ${tokens.danger}; }

    &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.warning .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
    &.danger  .card-content .thumb { color: ${tokens.danger}; opacity: 0.65; }
  }

  /* ============================================================
   * TOOLBAR — search + filter pills + atualizar
   * ============================================================ */
  .toolbar {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 22px;
    flex-wrap: wrap;
    flex-shrink: 0;

    .search-box {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 14px;
      background: ${({ theme }) => surface(theme, 0)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      min-width: 220px;
      max-width: 420px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      .search-icon {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 0.95em;
        flex-shrink: 0;
      }

      input {
        background: transparent;
        border: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        outline: none;
        flex: 1;
        letter-spacing: -0.005em;

        &::placeholder {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
        }
      }
    }

    .filter-pills {
      display: inline-flex;
      gap: 4px;
      padding: 4px;
      background: ${({ theme }) => surface(theme, 0)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};

      .filter-pill {
        ${mono};
        padding: 6px 12px;
        border: none;
        border-radius: ${tokens.radiusSm};
        background: transparent;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        font-size: 0.7em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        &:hover {
          opacity: 0.85;
          background: ${({ theme }) => surface(theme, 1)};
        }

        &.active {
          opacity: 1;
          background: ${({ theme }) => surface(theme, 2)};
          color: ${tokens.brand};
          box-shadow: inset 0 0 0 1px ${({ theme }) => hairline(theme, true)};
        }
      }
    }

    .action-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 9px 16px;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      background: ${({ theme }) => surface(theme, 0)};
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.82em;
      font-weight: 500;
      cursor: pointer;
      letter-spacing: -0.005em;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      margin-left: auto;

      &:hover {
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      &.primary {
        background: ${tokens.brand};
        border-color: ${tokens.brand};
        color: #fff;

        &:hover:not(:disabled) {
          background: ${tokens.brandHover};
          border-color: ${tokens.brandHover};
          transform: translateY(-1px);
        }
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none !important;
      }

      svg { font-size: 1em; flex-shrink: 0; }
    }
  }

  /* ============================================================
   * ERROR MESSAGE
   * ============================================================ */
  .error-message {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    margin-bottom: 18px;
    border: 1px solid rgba(239, 68, 68, 0.22);
    background: ${tokens.dangerSoft};
    border-radius: ${tokens.radiusMd};
    color: ${tokens.danger};
    font-size: 0.82em;
    font-weight: 500;
    flex-shrink: 0;

    svg { font-size: 1em; flex-shrink: 0; }
  }

  /* ============================================================
   * INDICES GRID — 4 colunas (igual Files .empresas-grid)
   * ============================================================ */
  .indices-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 2px 6px 24px 2px;
    margin: 0 -6px 0 -2px;
    align-content: start;

    /* Custom scrollbar discreta */
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: ${({ theme }) => surface(theme, 2)};
    }

    @media (max-width: 1279px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 1023px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 700px)  { grid-template-columns: 1fr; }
  }

  /* ============================================================
   * INDEX CARD (compact, 4 per row) — equivalente a .empresa-card-new
   * ============================================================ */
  .index-card-new {
    ${({ theme }) => cardBase(theme)};
    padding: 14px 16px;
    cursor: pointer;
    font-family: ${tokens.fontSans};
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    overflow: hidden;
    min-height: 170px;

    &:hover {
      transform: translateY(-1px);
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    &.archived {
      opacity: 0.7;
      border-style: dashed;

      &:hover { opacity: 1; }
    }

    /* --- Header --- */
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      .index-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;

        .index-name {
          font-size: 0.82em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.005em;
          line-height: 1.25;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-family: ${tokens.fontMono};
        }

        .index-sub {
          ${mono};
          font-size: 0.62em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          letter-spacing: 0.02em;
        }
      }

      .index-health {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
        ${mono};
        font-size: 0.6em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.08em;

        .health-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        &.green {
          color: ${tokens.brand};
          .health-dot {
            background: ${tokens.brand};
            box-shadow: 0 0 6px ${tokens.brandGlow};
            animation: ${pulseDot} 2.4s ${tokens.easeInOut} infinite;
          }
        }
        &.yellow {
          color: ${tokens.warn};
          .health-dot {
            background: ${tokens.warn};
            box-shadow: 0 0 6px rgba(245,158,11,0.45);
          }
        }
        &.red {
          color: ${tokens.danger};
          .health-dot {
            background: ${tokens.danger};
            box-shadow: 0 0 6px rgba(239,68,68,0.5);
          }
        }
        &.archived {
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          .health-dot {
            background: ${({ theme }) => theme.text};
            opacity: 0.4;
          }
        }
      }
    }

    /* --- Hero stats (docs + size) --- */
    .hero-stats {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      padding: 2px 0;

      .hero-stat {
        display: flex;
        flex-direction: column;
        gap: 1px;

        .hero-value {
          ${mono};
          font-size: 1.15em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        .hero-label {
          ${eyebrow};
          font-size: 0.55em;
          opacity: 0.4;
          margin-top: 4px;
        }
      }
    }

    /* --- Micro stats (shards · réplicas · status) --- */
    .micro-stats-inline {
      display: flex;
      align-items: center;
      gap: 10px;
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      ${mono};
      font-size: 0.66em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      letter-spacing: 0.01em;
      flex-wrap: wrap;

      .micro-inline {
        display: inline-flex;
        align-items: center;
        gap: 4px;

        .micro-key {
          opacity: 0.5;
          text-transform: uppercase;
          font-size: 0.92em;
          letter-spacing: 0.05em;
        }

        .micro-val {
          font-weight: 500;
          opacity: 1;
          color: ${({ theme }) => theme.text};
        }

        & + .micro-inline::before {
          content: "·";
          opacity: 0.35;
          margin-right: 4px;
        }
      }

      .status-pill {
        margin-left: auto;
        padding: 2px 8px;
        border-radius: ${tokens.radiusPill};
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        font-size: 0.95em;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        font-weight: 500;

        &.open {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
        }
        &.close {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239,68,68,0.25);
        }
      }
    }
  }

  /* ============================================================
   * EMPTY / LOADING STATE
   * ============================================================ */
  .empty-state {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5em 2em;
    text-align: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px dashed ${({ theme }) => hairline(theme, true)};
    border-radius: ${tokens.radiusLg};
    gap: 14px;

    svg {
      font-size: 1.8em;
      opacity: 0.4;
      color: ${({ theme }) => theme.text};
    }

    p {
      ${mono};
      margin: 0;
      font-size: 0.82em;
      opacity: 0.55;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.005em;
    }
  }
`;

/* ============================================================
 * SKELETON (loading card placeholder)
 * ============================================================ */
export const IndexCardSkeleton = styled.div`
  ${({ theme }) => cardBase(theme)};
  padding: 14px 16px;
  min-height: 158px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .sk {
    border-radius: ${tokens.radiusXs};
    background: linear-gradient(
      90deg,
      ${({ theme }) => surface(theme, 1)} 0%,
      ${({ theme }) => surface(theme, 2)} 50%,
      ${({ theme }) => surface(theme, 1)} 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s ${tokens.easeInOut} infinite;
  }

  .sk-header {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};

    .sk-name  { height: 12px; width: 60%; }
    .sk-badge { height: 10px; width: 50px; }
  }

  .sk-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    .sk-value { height: 18px; width: 70%; }
  }

  .sk-footer {
    height: 10px;
    width: 100%;
    margin-top: 8px;
  }
`;

/* ============================================================
 * MODAL OVERLAY (shared)
 * ============================================================ */
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 9, 12, 0.7);
  backdrop-filter: ${tokens.blurBg};
  -webkit-backdrop-filter: ${tokens.blurBg};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} ${tokens.durBase} ${tokens.easeOut};
  padding: 20px;
`;

/* ============================================================
 * INDEX SUMMARY MODAL — 640px (espelho de EmpresaModalContainer)
 * ============================================================ */
export const IndexSummaryModalContainer = styled.div`
  background: ${({ theme }) => theme.card};
  border-radius: ${tokens.radiusLg};
  width: 640px;
  max-width: 94%;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${tokens.shadowLg};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  animation: ${fadeIn} ${tokens.durSlow} ${tokens.easeOut};
  overflow: hidden;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${tokens.brandRing}, transparent);
  }

  /* ============ HEADER ============ */
  .modal-header {
    padding: 18px 22px 14px;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    .modal-title {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .eyebrow {
        ${mono};
        font-size: 0.58em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: ${tokens.brand};
        opacity: 0.65;
      }

      h2 {
        margin: 0;
        font-size: 0.98em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.015em;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: ${tokens.fontMono};
      }

      .modal-sub {
        ${mono};
        font-size: 0.66em;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
        margin-top: 1px;
        display: inline-flex;
        align-items: center;
        gap: 8px;

        .health-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }
        &.green  .health-dot { background: ${tokens.brand}; box-shadow: 0 0 6px ${tokens.brandGlow}; }
        &.yellow .health-dot { background: ${tokens.warn};  box-shadow: 0 0 6px rgba(245,158,11,0.45); }
        &.red    .health-dot { background: ${tokens.danger}; box-shadow: 0 0 6px rgba(239,68,68,0.5); }
        &.archived .health-dot { background: ${({ theme }) => theme.text}; opacity: 0.4; }
      }
    }

    .modal-close {
      width: 28px;
      height: 28px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: ${tokens.radiusSm};
      background: transparent;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      cursor: pointer;
      border: none;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      font-size: 0.95em;

      &:hover {
        opacity: 1;
        background: ${({ theme }) => surface(theme, 1)};
      }
    }
  }

  /* ============ BODY ============ */
  .modal-body {
    padding: 16px 22px 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  /* Section */
  .section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .section-eyebrow {
    ${mono};
    font-size: 0.6em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${({ theme }) => theme.text};
    opacity: 0.35;
    display: flex;
    align-items: center;
    gap: 8px;

    &::after {
      content: "";
      flex: 1;
      height: 1px;
      background: ${({ theme }) => hairline(theme)};
      opacity: 0.5;
    }
  }

  /* ============ Pairs (Visão Geral) ============ */
  .pairs-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    overflow: hidden;

    .pair-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 14px;
      background: ${({ theme }) => surface(theme, 0)};
      gap: 8px;

      &:nth-child(odd) {
        border-right: 1px solid ${({ theme }) => hairline(theme)};
      }
      &:nth-child(n+3) {
        border-top: 1px solid ${({ theme }) => hairline(theme)};
      }

      .pair-label {
        font-size: 0.74em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
      }

      .pair-value {
        ${mono};
        font-size: 0.76em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.85;
        text-align: right;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &.brand   { color: ${tokens.brand}; opacity: 1; }
        &.warn    { color: ${tokens.warn};  opacity: 1; }
      }

      .status-pill {
        ${mono};
        font-size: 0.62em;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: ${tokens.radiusPill};
        text-transform: uppercase;
        letter-spacing: 0.06em;
        border: 1px solid transparent;

        &.open {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
        }
        &.close {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239,68,68,0.22);
        }
        &.archived {
          background: ${({ theme }) => surface(theme, 1)};
          color: ${({ theme }) => theme.text};
          opacity: 0.55;
          border-color: ${({ theme }) => hairline(theme)};
        }
      }
    }
  }

  /* ============ Schema table ============ */
  .schema-table {
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    overflow: hidden;
    max-height: 240px;
    overflow-y: auto;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 2px;
    }

    .schema-row {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 12px;
      align-items: center;
      padding: 8px 14px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      &:last-child { border-bottom: none; }

      &.head {
        background: ${({ theme }) => surface(theme, 1)};

        span {
          ${eyebrow};
          font-size: 0.55em;
          opacity: 0.45;
        }
      }

      .field-name {
        ${mono};
        font-size: 0.72em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.85;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .field-type {
        ${mono};
        font-size: 0.62em;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: ${tokens.radiusPill};
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border: 1px solid ${tokens.brandRing};
        text-transform: lowercase;
        letter-spacing: 0.02em;
        flex-shrink: 0;
      }
    }
  }

  .empty-inline {
    ${mono};
    font-size: 0.7em;
    opacity: 0.4;
    color: ${({ theme }) => theme.text};
    text-align: center;
    padding: 16px;
    border: 1px dashed ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
  }

  /* ============ JSON preview (sample doc) ============ */
  .json-preview {
    background: ${({ theme }) => theme.body === "#fafbfc" ? "#1a1d29" : "#0a0c12"};
    border: 1px solid ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.06)"};
    border-radius: ${tokens.radiusMd};
    padding: 10px 14px;
    max-height: 220px;
    overflow: auto;
    position: relative;

    pre {
      margin: 0;
      ${mono};
      font-size: 0.7em;
      line-height: 1.55;
      color: rgba(226, 232, 240, 0.85);
      white-space: pre-wrap;
      word-break: break-word;
    }

    &::-webkit-scrollbar { width: 4px; height: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: rgba(255,255,255,0.1);
      border-radius: 2px;
    }
  }

  /* ============ Footer ============ */
  .modal-footer {
    padding: 12px 22px;
    border-top: 1px solid ${({ theme }) => hairline(theme)};
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    background: ${({ theme }) => surface(theme, 0)};
  }
`;

/* ============================================================
 * INDEX DOCS MODAL — 1100px (modal grande de documentos)
 * ============================================================ */
export const IndexDocsModalContainer = styled.div`
  background: ${({ theme }) => theme.card};
  border-radius: ${tokens.radiusLg};
  width: 1100px;
  max-width: 96%;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${tokens.shadowLg};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  animation: ${fadeIn} ${tokens.durSlow} ${tokens.easeOut};
  overflow: hidden;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${tokens.brandRing}, transparent);
  }

  /* ============ HEADER ============ */
  .docs-header {
    padding: 18px 24px 14px;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;

    .docs-title {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .eyebrow {
        ${mono};
        font-size: 0.58em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: ${tokens.brand};
        opacity: 0.65;
      }

      h2 {
        margin: 0;
        font-size: 1.02em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.015em;
        line-height: 1.2;
        font-family: ${tokens.fontMono};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .docs-meta {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        margin-top: 4px;
        ${mono};
        font-size: 0.68em;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;

        .docs-meta-item {
          display: inline-flex;
          align-items: center;
          gap: 5px;

          .meta-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            opacity: 0.7;
          }

          &.green .meta-dot { background: ${tokens.brand}; box-shadow: 0 0 5px ${tokens.brandGlow}; }
          &.yellow .meta-dot { background: ${tokens.warn}; }
          &.red    .meta-dot { background: ${tokens.danger}; }
        }

        .docs-meta-sep {
          opacity: 0.3;
        }
      }
    }

    .modal-close {
      width: 28px;
      height: 28px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: ${tokens.radiusSm};
      background: transparent;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      cursor: pointer;
      border: none;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      font-size: 0.95em;

      &:hover {
        opacity: 1;
        background: ${({ theme }) => surface(theme, 1)};
      }
    }
  }

  /* ============ BODY ============ */
  .docs-body {
    flex: 1;
    overflow-y: auto;
    padding: 18px 24px 22px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* SearchBar */
  .docs-search {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    transition: border-color ${tokens.durBase} ${tokens.easeOut};

    &:focus-within { border-color: ${tokens.brandRing}; }

    svg {
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      font-size: 1em;
      flex-shrink: 0;
    }

    input {
      background: transparent;
      border: none;
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.85em;
      outline: none;
      flex: 1;
      letter-spacing: -0.005em;

      &::placeholder {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
      }
    }
  }

  /* Documents list */
  .documents-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .doc-card {
    ${({ theme }) => cardBase(theme)};
    padding: 14px 18px;
    cursor: pointer;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .doc-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      margin-bottom: 10px;

      .doc-id-group {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
        flex: 1;

        .doc-id {
          ${mono};
          font-size: 0.7em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.85;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .doc-date {
          ${mono};
          font-size: 0.62em;
          color: ${({ theme }) => theme.text};
          opacity: 0.45;
          display: inline-flex;
          align-items: center;
          gap: 5px;

          svg { font-size: 0.95em; opacity: 0.6; }
        }
      }

      .doc-score {
        ${mono};
        font-size: 0.62em;
        font-weight: 500;
        padding: 3px 8px;
        border-radius: ${tokens.radiusPill};
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border: 1px solid ${tokens.brandRing};
        flex-shrink: 0;
      }
    }

    .doc-preview {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px 14px;

      .field {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;

        .field-name {
          ${eyebrow};
          font-size: 0.55em;
          opacity: 0.4;
        }

        .field-value {
          ${mono};
          font-size: 0.7em;
          color: ${({ theme }) => theme.text};
          opacity: 0.8;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .doc-full {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      background: ${({ theme }) => theme.body === "#fafbfc" ? "#1a1d29" : "#0a0c12"};
      border-radius: ${tokens.radiusMd};
      padding: 12px 14px;
      max-height: 400px;
      overflow: auto;

      pre {
        margin: 0;
        ${mono};
        font-size: 0.7em;
        line-height: 1.55;
        color: rgba(226, 232, 240, 0.85);
        white-space: pre-wrap;
        word-break: break-word;
      }

      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-track { background: transparent; }
      &::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.1);
        border-radius: 2px;
      }
    }
  }

  .docs-footer-info {
    ${mono};
    font-size: 0.72em;
    text-align: center;
    padding: 16px;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
    letter-spacing: -0.005em;
  }

  .docs-loading {
    ${mono};
    text-align: center;
    padding: 40px;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    font-size: 0.82em;
    letter-spacing: -0.005em;
  }
`;

/* ============================================================
 * ACTION BUTTON (modal footer)
 * ============================================================ */
export const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: ${tokens.radiusMd};
  font-weight: 500;
  font-size: 0.82em;
  font-family: ${tokens.fontSans};
  cursor: pointer;
  border: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ $primary, $danger, $info, theme }) => {
    if ($primary) return tokens.brand;
    if ($danger) return tokens.dangerSoft;
    if ($info) return tokens.infoSoft;
    return surface(theme, 0);
  }};
  color: ${({ $primary, $danger, $info, theme }) => {
    if ($primary) return "#fff";
    if ($danger) return tokens.danger;
    if ($info) return tokens.info;
    return theme.text;
  }};
  border-color: ${({ $primary, $danger, $info, theme }) => {
    if ($primary) return tokens.brand;
    if ($danger) return "rgba(239, 68, 68, 0.25)";
    if ($info) return "rgba(59, 130, 246, 0.25)";
    return hairline(theme);
  }};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  letter-spacing: -0.005em;

  &:hover:not(:disabled) {
    ${({ $primary }) => $primary && `transform: translateY(-1px);`}
    background: ${({ $primary, $danger, $info, theme }) => {
      if ($primary) return tokens.brandHover;
      if ($danger) return "rgba(239, 68, 68, 0.18)";
      if ($info) return "rgba(59, 130, 246, 0.18)";
      return surface(theme, 1);
    }};
    border-color: ${({ $primary, $danger, $info, theme }) => {
      if ($primary) return tokens.brandHover;
      if ($danger) return "rgba(239, 68, 68, 0.4)";
      if ($info) return "rgba(59, 130, 246, 0.4)";
      return hairline(theme, true);
    }};
  }

  &:active { transform: translateY(0); }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  svg { font-size: 1em; flex-shrink: 0; }

  .spin { animation: ${spinKf} 1s linear infinite; }
`;
